import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { themes, defaultTheme, brandConfig } from '../config/theme';

// Create Context
const ThemeContext = createContext(null);

/**
 * Apply theme colors to CSS variables
 */
const applyThemeColors = (theme) => {
  const root = document.documentElement;
  const colors = theme.colors;
  
  Object.entries(colors).forEach(([key, value]) => {
    // Convert camelCase to kebab-case
    const cssVarName = `--color-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
    root.style.setProperty(cssVarName, value);
  });
  
  // Set data-theme attribute
  root.setAttribute('data-theme', theme.name);
};

/**
 * ThemeProvider component
 * Provides theme state management and switching functionality
 */
export const ThemeProvider = ({ children }) => {
  // Get saved theme from localStorage, default to defaultTheme
  const [themeName, setThemeName] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved && themes[saved] ? saved : defaultTheme;
  });
  
  // Current theme configuration
  const theme = themes[themeName];

  // Apply theme
  useEffect(() => {
    applyThemeColors(theme);
    localStorage.setItem('theme', themeName);
  }, [themeName, theme]);

  // Toggle theme
  const toggleTheme = useCallback(() => {
    setThemeName(prev => prev === 'light' ? 'dark' : 'light');
  }, []);

  // Set specific theme
  const setTheme = useCallback((name) => {
    if (themes[name]) {
      setThemeName(name);
    }
  }, []);

  // Check if dark theme
  const isDark = themeName === 'dark';

  const value = {
    // Current theme name
    themeName,
    // Current theme configuration
    theme,
    // Brand configuration
    brand: brandConfig,
    // Is dark mode
    isDark,
    // Toggle theme
    toggleTheme,
    // Set specific theme
    setTheme,
    // All available themes
    availableThemes: Object.keys(themes),
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * useTheme Hook
 * Get theme configuration and methods
 */
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

/**
 * useBrand Hook
 * Get only brand configuration
 */
export const useBrand = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useBrand must be used within ThemeProvider');
  }
  return context.brand;
};

/**
 * useColors Hook
 * Get only color configuration
 */
export const useColors = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useColors must be used within ThemeProvider');
  }
  return context.theme.colors;
};

export default ThemeContext;
